import SparkleGray from "@/assets/icons/SparkleGray.svg";
import ArrowLeftCircle from "@/assets/icons/ArrowLeftCircle.svg";
import DescriptionIcon from "@/assets/icons/Description.svg";
import EditIcon from "@/assets/icons/Edit.svg";
import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { InfoIcon } from "lucide-react";
import ModalGenerateScriptSkeleton from "./ModalGenerateScriptSkeleton";

interface ScriptData {
  ad_format: string;
  brand: string;
  call_to_action: string;
  country: string;
  duration: string;
  hook: string;
  inserted_at: string;
  main_content: string;
  product: string;
  script_type: string;
  title: string;
  video_id: string;
  visual_cues: string[];
  voiceover_script: string;
}

interface ModalGenerateScriptProps {
  currentTab: "videoDetails" | "generateScript" | "videoAnalysis";
  setCurrentTab: Dispatch<
    SetStateAction<"videoDetails" | "generateScript" | "videoAnalysis">
  >;
  setIsSelectModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsDetailsModalOpen: Dispatch<SetStateAction<boolean>>;
  scripts: ScriptData[];
  isLoading?: boolean;
}

const ModalGenerateScript = ({
  setCurrentTab,
  setIsSelectModalOpen,
  setIsDetailsModalOpen,
  scripts,
  isLoading,
  currentTab,
}: ModalGenerateScriptProps) => {
  const [selectedScriptIndex, setSelectedScriptIndex] = useState(0);
  const [scriptTab, setScriptTab] = useState<"scriptOutline" | "details">(
    "scriptOutline"
  );

  const currentScript = scripts[selectedScriptIndex];

  const handlerProceed = () => {
    setIsDetailsModalOpen(false);
    setIsSelectModalOpen(true);
  };

  if (isLoading) {
    return <ModalGenerateScriptSkeleton />;
  }

  if (!scripts || scripts.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>No scripts found for this video.</p>
      </div>
    );
  }

  const formatLabel = (text?: string | null) => {
    if (!text) return "N/A"; // or a more suitable fallback
    return text
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div>
      <div
        onClick={() => setCurrentTab("videoDetails")}
        className="bg-white border border-[#E4E7EC] px-3 py-0.5 items-center gap-1 rounded-lg hover:bg-gray-50 inline-flex mb-5 font-medium cursor-pointer"
      >
        <ArrowLeftCircle />
        <span className="text-xs font-medium text-[#344054]">Go Back</span>
      </div>

      <div className="flex justify-center items-center gap-x-2 flex-wrap">
        {scripts.map((_, index) => (
          <div
            key={index}
            onClick={() => setSelectedScriptIndex(index)}
            className={`flex items-center gap-x-2 px-2 py-1 rounded-[4px] text-sm font-medium cursor-pointer leading-5 tracking-[-0.35px] ${
              selectedScriptIndex === index
                ? "bg-[#1B1B1B] text-white"
                : "bg-[#F2F4F7] text-black"
            }`}
          >
            {selectedScriptIndex !== index && <SparkleGray />}
            Script {index + 1}
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-x-1 mt-5">
          <h5 className="text-[#000000] text-base font-semibold leading-[125%] tracking-[-0.12px] mb-5">
            {currentScript.title}
          </h5>
        </div>
        <div className="flex gap-x-5">
          <div className="text-xs leading-5 tracking-[-1%] font-medium border border-[#E4E7EC] rounded-lg px-[10.5px] py-[1.5px]">
            <span className="text-[#34405480]">Script Type:</span>
            <span className="text-[#344054]">
              {formatLabel(currentScript.script_type || "No script provided")}
            </span>
          </div>
          <div className="text-[#708CB0] text-xs font-medium leading-5 tracking-[-1%] border border-[#D9EBF8] rounded-lg bg-[#EDF6FC] py-[3.5px] px-3">
            Duration: {currentScript.duration}
          </div>
          <div className="text-xs leading-5 tracking-[-1%] font-medium border border-[#E4E7EC] rounded-lg px-[10.5px] py-[1.5px]">
            <span className="text-[#34405480]">Ad Format:</span>
            <span className="text-[#344054]">
              {" "}
              {formatLabel(currentScript.ad_format || "No ad format")}
            </span>
          </div>
        </div>

        {/* Script Tabs */}
        <div className="mt-5 flex items-center gap-x-4">
          <div
            onClick={() => setScriptTab("scriptOutline")}
            className={`flex gap-1 items-center cursor-pointer pb-2 ${
              scriptTab === "scriptOutline" && "border-b-[3px] border-gray-900"
            }`}
          >
            <DescriptionIcon />
            <p className="font-semibold text-xs">Script Outline</p>
          </div>
          <div
            onClick={() => setScriptTab("details")}
            className={`flex gap-1 items-center cursor-pointer pb-2 ${
              scriptTab === "details" && "border-b-[3px] border-gray-900"
            }`}
          >
            <InfoIcon className="h-4" />
            <p className="font-semibold text-xs">Details Outline</p>
          </div>
        </div>

        {scriptTab === "scriptOutline" && (
          <div>
            {/* <ScriptSection
              key={`hook-${selectedScriptIndex}`}
              title="Hook"
              content={currentScript.hook || "No hook provided"}
            />
            <ScriptSection
              key={`main-content-${selectedScriptIndex}`}
              title="Main Content"
              content={currentScript.main_content || "No main content provided"}
            /> */}
            <ScriptSection
              key={`voiceover-${selectedScriptIndex}`}
              title="Voiceover Script"
              content={
                currentScript.voiceover_script || "No voiceover script provided"
              }
            />
            <ScriptSection
              key={`cta-${selectedScriptIndex}`}
              title="Call to Action"
              content={formatLabel(
                currentScript.call_to_action || "No CTA provided"
              )}
            />
            <ScriptSection
              key={`visuals-${selectedScriptIndex}`}
              title="Visual Cues"
              content={
                Array.isArray(currentScript.visual_cues)
                  ? currentScript.visual_cues.join("\n\n")
                  : currentScript.visual_cues || "No visual cues provided"
              }
            />
          </div>
        )}

        {scriptTab === "details" && (
          <div className="space-y-3 mt-5">
            <ScriptSection
              key={`brand-${selectedScriptIndex}`}
              title="Brand & Product"
              content={`${currentScript.brand || "Unknown Brand"} - ${
                currentScript.product || "Unknown Product"
              }`}
            />
            <ScriptSection
              key={`country-${selectedScriptIndex}`}
              title="Country"
              content={currentScript.country || "No country specified"}
            />

            <ScriptSection
              key={`ad-format-detail-${selectedScriptIndex}`}
              title="Ad Format Details"
              content={formatLabel(
                currentScript.ad_format || "No ad format found"
              )}
            />
            <ScriptSection
              key={`script-type-detail-${selectedScriptIndex}`}
              title="Script Type Details"
              content={formatLabel(
                currentScript.script_type || "No script type found"
              )}
            />
          </div>
        )}
      </div>

      <div className="mt-3">
        <CustomButton
          onClick={handlerProceed}
          backgroundColor="#1B1B1B"
          padding="12px"
          borderRadius="8px"
          color="white"
          fontSize="20px"
          fontWeight="600"
          className="leading-6 tracking-[0%] w-full"
        >
          Proceed with this Structure
        </CustomButton>
      </div>
    </div>
  );
};

const ScriptSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
  };

  return (
    <div className="border border-[#DDDDDD] rounded-lg p-3 mt-5">
      <div className="flex justify-between items-center">
        <h6 className="text-[#6D6D6D] font-bold text-sm leading-6 tracking-[0%]">
          {title}
        </h6>
        <div
          className="border border-[#DCDEE0] px-2 py-1 rounded-[4px] flex items-center gap-x-1 cursor-pointer"
          onClick={handleEditClick}
        >
          <EditIcon />
          <p className="text-sm font-medium leading-5 tracking-[-2.5%]">Edit</p>
        </div>
      </div>
      {isEditing ? (
        <textarea
          value={editedContent}
          onChange={handleChange}
          onBlur={handleBlur}
          className="w-full p-2 mt-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          autoFocus
          rows={4}
        />
      ) : (
        <p className="text-[#6D6D6D] text-sm leading-6 tracking-[0%] mt-3 whitespace-pre-wrap">
          {editedContent}
        </p>
      )}
    </div>
  );
};

export default ModalGenerateScript;
